import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requiredRole?: 'buyer' | 'seller' | 'admin';
  /**
   * 최소한의 로딩만 보여주고 빠르게 넘어갈지 여부
   */
  minimalLoading?: boolean;
}

export function ProtectedRoute({ 
  children, 
  redirectTo = '/login',
  requiredRole,
  minimalLoading = false
}: ProtectedRouteProps) {
  const { user, isLoading, error, retryAuth } = useAuth();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [showMinimalLoading, setShowMinimalLoading] = useState(isLoading);

  useEffect(() => {
    // 로딩이 완료된 후에만 리다이렉트 판단
    if (!isLoading) {
      if (!user) {
        setShouldRedirect(true);
      } else if (requiredRole && user.role !== requiredRole) {
        setShouldRedirect(true);
      } else {
        setShouldRedirect(false);
      }
      
      // 최소한의 로딩 시간 후 로딩 화면 제거
      if (minimalLoading) {
        const timer = setTimeout(() => {
          setShowMinimalLoading(false);
        }, 150); // 매우 짧은 시간만 로딩 표시
        
        return () => clearTimeout(timer);
      } else {
        setShowMinimalLoading(false);
      }
    } else {
      setShowMinimalLoading(isLoading);
    }
  }, [user, isLoading, requiredRole, minimalLoading]);

  // 최소한의 로딩 또는 일반 로딩 중일 때
  if (isLoading || (minimalLoading && showMinimalLoading)) {
    if (minimalLoading) {
      // 최소한의 로딩 - 작은 인디케이터만 표시
      return (
        <div className="min-h-screen bg-white">
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
              <span className="text-sm text-gray-600">확인 중...</span>
            </div>
          </div>
        </div>
      );
    }
    
    // 기본 로딩 화면
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
          <p className="text-[#666666] text-base">로그인 정보 확인 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (error && !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 max-w-sm px-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">인증 오류</h3>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
          </div>
          <div className="flex gap-3 w-full">
            <button
              onClick={retryAuth}
              className="flex-1 bg-[#86DB89] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#75C878] transition-colors"
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.href = '/login'}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              로그인
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 인증이 필요하지만 로그인되지 않은 경우
  if (shouldRedirect && !user) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ 
          message: '로그인이 필요한 서비스입니다.',
          from: location.pathname 
        }} 
        replace 
      />
    );
  }

  // 특정 역할이 필요하지만 권한이 없는 경우
  if (shouldRedirect && user && requiredRole && user.role !== requiredRole) {
    return (
      <Navigate 
        to="/" 
        state={{ 
          message: `${requiredRole === 'seller' ? '사장님' : '구매자'} 전용 서비스입니다.` 
        }} 
        replace 
      />
    );
  }

  // 인증된 사용자인 경우 컴포넌트 렌더링
  return <>{children}</>;
}